import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import type {
    ExchangeRateConvertResponse,
    StoredRatesResponse,
    ExchangeRateCircuitState,
    ExchangeRateCircuitResetResponse,
} from '../types';

const QUERY_KEY = 'exchange-rates';

export interface ConvertCurrencyParams {
    amount: number;
    from: string;
    to: string;
    timestamp?: number;
    date?: string; // Y-m-d format
}

/**
 * Convert a Y-m-d date string to a Unix timestamp at midnight UTC.
 */
function dateToTimestamp(date: string): number {
    const parts = date.split('-').map(Number);
    const year = parts[0] ?? 0;
    const month = parts[1] ?? 1;
    const day = parts[2] ?? 1;
    return Date.UTC(year, month - 1, day) / 1000;
}

export function useConvertCurrency() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: ConvertCurrencyParams) => {
            const { date, ...rest } = params;
            const body = {
                ...rest,
                timestamp: date ? dateToTimestamp(date) : rest.timestamp,
            };

            const response = await fetch(
                `${import.meta.env.VITE_API_URL || ''}/api/exchange-rate/convert`,
                {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                    },
                    body: JSON.stringify(body),
                },
            );

            return response.json() as Promise<ExchangeRateConvertResponse>;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'stored'] });
        },
    });
}

export function useStoredRates(timestamp?: number, limit = 50, offset = 0) {
    return useQuery({
        queryKey: [QUERY_KEY, 'stored', timestamp, limit, offset],
        queryFn: () => {
            const searchParams = new URLSearchParams({
                limit: limit.toString(),
                offset: offset.toString(),
            });
            if (timestamp) {
                searchParams.set('timestamp', timestamp.toString());
            }
            return api.get<StoredRatesResponse>(
                `/api/exchange-rate-admin/stored?${searchParams}`,
            );
        },
        refetchInterval: 10000,
    });
}

export function useExchangeRateCircuitState() {
    return useQuery({
        queryKey: [QUERY_KEY, 'circuit-state'],
        queryFn: () =>
            api.get<ExchangeRateCircuitState>('/api/exchange-rate-admin/circuit-state'),
        refetchInterval: 5000,
    });
}

export function useResetExchangeRateCircuit() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: () =>
            api.post<ExchangeRateCircuitResetResponse>(
                '/api/exchange-rate-admin/circuit-reset',
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        },
    });
}
